import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { MessageSquare, Send, User, Bot, Search } from "lucide-react";

export default function Conversations() {
  const { t } = useTranslation();
  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState("");
  const messagesEnd = useRef(null);

  const fetchConversations = async () => {
    try {
      const { data } = await api.get("/conversations");
      setConversations(data);
    } catch (e) {
      toast.error(t("convo.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchConversations(); }, []);

  // Live-ish refresh: poll the list every 8s and the open conversation every 4s,
  // so inbound/agent messages appear without manual reload (kills the "lag").
  useEffect(() => {
    const t = setInterval(fetchConversations, 8000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!selected) return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/conversations/${selected}`);
        setDetail(data);
      } catch { /* silent poll */ }
    }, 4000);
    return () => clearInterval(t);
  }, [selected]);

  const selectConvo = async (convo) => {
    setSelected(convo.id);
    try {
      const { data } = await api.get(`/conversations/${convo.id}`);
      setDetail(data);
    } catch (e) {
      toast.error(t("convo.loadOneFailed"));
    }
  };

  useEffect(() => {
    messagesEnd.current?.scrollIntoView({ behavior: "smooth" });
  }, [detail?.messages]);

  const sendMessage = async () => {
    if (!message.trim() || !selected) return;
    setSending(true);
    try {
      const { data } = await api.post(`/conversations/${selected}/messages`, {
        content: message, role: "user"
      });
      setDetail(data);
      setMessage("");
    } catch (e) {
      toast.error(t("convo.sendFailed"));
    } finally {
      setSending(false);
    }
  };

  const filtered = conversations.filter(c =>
    (c.contact_name || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div data-testid="conversations-page" className="flex h-full">
      {/* Sidebar - Conversation List */}
      <div className="w-80 border-r border-border flex flex-col shrink-0 bg-gray-50">
        <div className="p-4 border-b border-border">
          <h2 className="text-lg font-['Manrope'] font-bold text-foreground mb-3">Conversations</h2>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input
              data-testid="search-conversations"
              placeholder={t("convo.search")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-8 bg-secondary/50 border-transparent focus:border-primary text-sm"
            />
          </div>
        </div>
        <ScrollArea className="flex-1">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-4 text-center text-sm text-muted-foreground">{t("convo.noConversations")}</div>
          ) : (
            filtered.map((c) => (
              <div
                key={c.id}
                data-testid={`convo-item-${c.id}`}
                className={`flex items-center gap-3 px-4 py-3 cursor-pointer border-b border-border transition-colors duration-200 ${
                  selected === c.id ? "bg-white/5" : "hover:bg-white/[0.02]"
                }`}
                onClick={() => selectConvo(c)}
              >
                <div className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center shrink-0">
                  <User className="w-4 h-4 text-muted-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-foreground truncate">{c.contact_name || t("convo.unknown")}</p>
                    <Badge variant="outline" className="text-[10px] border-border text-muted-foreground ml-2 shrink-0">
                      {c.channel}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground truncate mt-0.5">
                    {c.messages && c.messages.length > 0 ? c.messages[c.messages.length - 1]?.content : t("convo.noMessages")}
                  </p>
                </div>
              </div>
            ))
          )}
        </ScrollArea>
      </div>

      {/* Chat Area */}
      <div className="flex-1 flex flex-col">
        {!selected ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center">
            <MessageSquare className="w-16 h-16 text-muted-foreground/20 mb-4" />
            <p className="text-lg font-semibold text-muted-foreground">Select a conversation</p>
            <p className="text-sm text-muted-foreground/60 mt-1">Choose from the list to view messages</p>
          </div>
        ) : (
          <>
            {/* Chat header */}
            <div className="h-14 border-b border-border flex items-center px-6 shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="w-4 h-4 text-primary" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">{detail?.contact_name || "Unknown"}</p>
                  <p className="text-xs text-muted-foreground">{detail?.contact_email || detail?.contact_phone || detail?.channel}</p>
                </div>
              </div>
              <div className="ml-auto flex items-center gap-2">
                {detail?.lead_qualified && <Badge className="bg-primary/10 text-primary border-primary/30">Qualified</Badge>}
                {detail?.appointment_booked && <Badge className="bg-[#BF5AF2]/10 text-[#BF5AF2] border-[#BF5AF2]/30">Booked</Badge>}
              </div>
            </div>

            {/* Messages */}
            <ScrollArea className="flex-1 p-6">
              <div className="space-y-4 max-w-3xl mx-auto">
                {(detail?.messages || []).map((msg, i) => (
                  <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"} animate-fade-in`}>
                    {msg.role !== "user" && (
                      <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center mr-2 mt-1 shrink-0">
                        <Bot className="w-3.5 h-3.5 text-primary" />
                      </div>
                    )}
                    <div
                      className={
                        msg.role === "user"
                          ? "bg-primary text-primary-foreground rounded-2xl rounded-tr-sm px-4 py-2.5 max-w-[75%]"
                          : "bg-secondary text-secondary-foreground rounded-2xl rounded-tl-sm px-4 py-2.5 max-w-[75%]"
                      }
                      data-testid={`msg-${i}`}
                    >
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                      <p className="text-[10px] opacity-50 mt-1">
                        {msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString() : ""}
                      </p>
                    </div>
                  </div>
                ))}
                <div ref={messagesEnd} />
              </div>
            </ScrollArea>

            {/* Input */}
            <div className="border-t border-border p-4 shrink-0">
              <div className="flex gap-2 max-w-3xl mx-auto">
                <Input
                  data-testid="chat-input"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={t("convo.typeMessage")}
                  className="bg-secondary/50 border-transparent focus:border-primary"
                  onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && sendMessage()}
                />
                <Button
                  data-testid="chat-send-btn"
                  onClick={sendMessage}
                  disabled={sending || !message.trim()}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 active:scale-95 transition-transform duration-150"
                >
                  {sending ? (
                    <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
